import { DateTime } from 'luxon';
import { Carrier } from 'smg/customer-api/carriers';
import * as models from 'smg/customer-api/models';
import { RemToPx } from 'smg/rem';
import * as column from 'smg/table/column';
import * as tableLib from 'smg/table/table';
import { AccountReady } from '../account-session';
import * as search from '../search';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    type r = models.Shipment;
    type c = keyof r;
    type d = never;

    class Table extends tableLib.Table<r, c, d> {
        rowHeight = RemToPx(parseFloat(getComputedStyle(document.querySelector('.iap-table') as HTMLElement).getPropertyValue('--row-height'))) + 1;

        rowsPerPage = 100;

        getRowsFromResponse = (d: unknown) => {
            const data = models.ShipmentsHyejooResponse.fromJSON(d);
            return data.results;
        };

        constructor() {
            super('/v2/shipments', search.Input as HTMLInputElement, search.Form as HTMLFormElement);

            this.addColumns(
                new column.ColumnID(),
                new column.Column({
                    name: 'Tracking Number',
                    width: '16rem',
                    columns: ['trackingNumber', 'carrierID'],
                    draw: async (td, shipment) => {
                        if (!shipment.trackingNumber) {
                            return;
                        }

                        const carrier = await Carrier(shipment.carrierID);
                        if (!carrier?.link) {
                            td.textContent = shipment.trackingNumber;
                            return;
                        }

                        const a = document.createElement('a');
                        a.href = carrier.trackingURL(shipment.trackingNumber);
                        a.textContent = shipment.trackingNumber;

                        td.appendChild(a);
                    },
                    text: shipment => shipment.trackingNumber,
                }),
                new column.Column({
                    name: 'Carrier',
                    width: '10rem',
                    columns: ['carrierID'],
                    text: shipment => Carrier(shipment.carrierID).then(carrier => carrier?.name || ''),
                }),
                new column.Column({
                    name: 'Status',
                    width: 10,
                    columns: ['shippedAt', 'deliveredAt'],
                    draw: (td, shipment) => {
                        if (shipment.deliveredAt.isValid) {
                            td.classList.add('text-success');
                            td.textContent = `Delivered - ${shipment.deliveredAt.toLocaleString(DateTime.DATETIME_SHORT)}`;
                            return;
                        } else if (shipment.shippedAt.isValid) {
                            td.textContent = `Shipped - ${shipment.shippedAt.toLocaleString(DateTime.DATETIME_SHORT)}`;
                            return;
                        }

                        td.textContent = 'Not Yet Shipped';
                    },
                }),
                new column.ColumnNumber({
                    name: 'Weight',
                    column: 'weightLBS',
                    width: '5rem',
                    text: shipment => {
                        if (shipment.weightLBS.eq(0)) {
                            return '';
                        }

                        return `${shipment.weightLBS.round(1).toString()}lbs`;
                    },
                }),
                new column.Column({
                    name: 'Order',
                    width: '10rem',
                    columns: 'order',
                    draw: (td, shipment) => {
                        if (!shipment.order) {
                            return;
                        }

                        td.classList.add('d-flex', 'flex-direction-column');

                        const a = document.createElement('a');
                        a.textContent = `#${shipment.order.invoiceNumber}`;
                        a.href = `/orders/?q=${shipment.order.invoiceNumber}`;

                        td.appendChild(a);
                    },
                    text: shipment => String(shipment.order?.invoiceNumber || ''),
                }),
                new column.ColumnDateTime({
                    name: 'Date',
                    width: '7rem',
                    sortDirection: 'desc',
                    value: shipment => shipment.created.isValid ? shipment.created.toISO() ?? '' : '',
                }),
            );

            this.enableAutoUpdating(10 * 60 * 1000);
        }
    }

    const t = new Table();

    void t.build();
}
